import { Button, Column, Icon, Page, RowId } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect } from 'react';
import { useDeepCompareEffect, useToggle, useUpdateEffect } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { ConversationsBulkToolbar } from 'components/conversation/conversationsBulkToolbar/conversationsBulkToolbar';
import { useConversationEventsEffect } from 'utils/conversation/useConversationEventsEffect';
import { useSelectConversations } from 'utils/conversation/useSelectConversations/useSelectConversations';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ConversationsFiltersToolbar } from './components/filtersToolbar/conversationsFiltersToolbar';
import { ConversationsTable } from './components/table/conversationsTable';
import { conversationsActions } from './conversations.actions';
import styles from './conversations.view.module.scss';
import { conversationMatchesSearchParams } from './utils/conversationMatchesSearchParams/conversationMatchesSearchParams';
import { useConversations } from './utils/useConversations/useConversations';
import { useConversationsParamsToApiArg } from './utils/useConversationsParamsToApiArg/useConversationsParamsToApiArg';

/** Represents the conversations view component. */
export const ConversationsView = (): JSX.Element => {
  const { hasBulkOperations } = useFeatureFlag(['has-bulk-operations']);
  const dispatch = useAppDispatch();
  const {
    asyncStatus: conversationsAsyncStatus,
    conversations,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
  } = useConversations();

  const { params } = useAppSearchParams('/conversations');
  const {
    paramsConversationsApiArg,
    paramsPaginatedApiArg,
    paramsSortedApiArg,
  } = useConversationsParamsToApiArg();

  const {
    asyncStatus: selectConversationsAsyncStatus,
    eligibleBulkConversationIds,
    isCurrentPageAllConversationsSelected,
    isCurrentPageSomeConversationsSelected,
    selectedConversationIds,
    totalFilteredConversations,
    clearSelectedConversations,
    selectAllCurrentPageConversation,
    selectAllEligibleConversations,
    deselectConversation,
    toggleSelectConversation,
    resetSelection,
  } = useSelectConversations({
    currentPageConversationIds: conversations.map(
      ({ conversation: { id } }) => id,
    ),
    onFetchConversationIdsBulk: async () => {
      const { data } = await dispatch(
        conversationsApi.endpoints.readConversationsIdsBulk.initiate(
          paramsConversationsApiArg,
        ),
      ).unwrap();

      return data;
    },
  });

  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const [hasCollapsedToolbars, toggleHasCollapseToolbars] = useToggle(
    !!isSmallScreen,
  );

  useUpdateEffect(() => {
    toggleHasCollapseToolbars(!!isSmallScreen);
  }, [isSmallScreen]);

  // Handles re-setting the selection when conversation api arg related search
  // params change. This excludes the paginated api arg as these will change
  // during pagination and we want to preserve the selection when that occurs.
  // Note that we employ deep compare effect as we don't want to rely on
  // reference equality.
  useDeepCompareEffect(() => resetSelection(), [paramsConversationsApiArg]);

  // Handles conversations coming through the WebSocket. We do this to remove a
  // conversation from list of selected conversations if incoming conversation
  // no longer matches search params.
  useConversationEventsEffect(({ params: webSocketParams }) => {
    const { id } = webSocketParams.conversation;
    const isIncomingConversationSelected = selectedConversationIds.includes(id);
    const isIncomingConversationMatchesParams = conversationMatchesSearchParams(
      webSocketParams.conversation,
      params,
    );

    if (
      !isIncomingConversationMatchesParams &&
      isIncomingConversationSelected
    ) {
      deselectConversation(id);
    }
  });

  const showBulkToolbar = selectedConversationIds.length > 0;

  /** Handles selecting a row in the table. */
  const handleRowSelect = useCallback(
    (rowId: RowId) => {
      toggleSelectConversation(rowId as number);

      ampli.conversationsSelectClicked({
        csid: rowId as number,
        selected: selectedConversationIds.includes(rowId as number),
      });
    },
    [toggleSelectConversation, selectedConversationIds],
  );

  /** Handles selecting all current rows in the table. */
  const handleAllRowsSelect = useCallback(() => {
    selectAllCurrentPageConversation();

    ampli.conversationsSelectAllClicked();
  }, [selectAllCurrentPageConversation]);

  /** Handles navigating to the previous page. */
  const handlePreviousPage = useCallback(() => {
    previousPage();

    ampli.conversationsPreviousPageClicked();
  }, [previousPage]);

  /** Handles navigating to the next page. */
  const handleNextPage = useCallback(() => {
    nextPage();

    ampli.conversationsNextPageClicked();
  }, [nextPage]);

  // Handles setting the last search params.
  useEffect(() => {
    dispatch(
      conversationsActions.setLastSearchParams({
        apiArg: {
          ...paramsConversationsApiArg,
          ...paramsPaginatedApiArg,
          ...paramsSortedApiArg,
        },
      }),
    );
  }, [
    dispatch,
    paramsConversationsApiArg,
    paramsPaginatedApiArg,
    paramsSortedApiArg,
  ]);

  const toolbar = (
    <div
      className={classnames(styles.toolbars, {
        [styles.collapsed]: hasCollapsedToolbars,
      })}
    >
      {hasBulkOperations && (
        <ConversationsBulkToolbar
          className={classnames(styles.toolbar, styles.bulkActions, {
            [styles.active]: showBulkToolbar,
          })}
          location="conversations"
          asyncStatus={selectConversationsAsyncStatus}
          eligibleBulkConversationIds={eligibleBulkConversationIds}
          selectedConversationIds={selectedConversationIds}
          totalFilteredConversations={totalFilteredConversations}
          onClearAll={clearSelectedConversations}
          onSelectAll={selectAllEligibleConversations}
        />
      )}

      <ConversationsFiltersToolbar
        className={classnames(styles.toolbar, styles.filters, {
          [styles.active]: !showBulkToolbar,
        })}
      />
    </div>
  );

  const toggleToolbarButton = isSmallScreen ? (
    <Button
      variant="tertiary"
      slotStart={<Icon name={hasCollapsedToolbars ? 'plus' : 'minus'} />}
      onClick={toggleHasCollapseToolbars}
    >
      {hasCollapsedToolbars ? 'Show filters' : 'Hide filters'}
    </Button>
  ) : null;

  return (
    <Page>
      <Page.Head
        title="Conversations"
        controls={isSmallScreen ? toggleToolbarButton : toolbar}
      />

      {isSmallScreen && (
        <Column.Row noVerticalPadding noMinHeight>
          {toolbar}
        </Column.Row>
      )}

      <Page.Body>
        <Page.Section>
          <ConversationsTable
            items={conversations}
            asyncStatus={conversationsAsyncStatus}
            selectableRowsProps={{
              selectedRowIds: selectedConversationIds as number[],
              allRowsSelected:
                conversationsAsyncStatus === 'fulfilled' &&
                isCurrentPageAllConversationsSelected,
              allRowsSelectedIndeterminate:
                conversationsAsyncStatus === 'fulfilled' &&
                isCurrentPageSomeConversationsSelected,
              onRowSelect: handleRowSelect,
              onAllRowsSelect: handleAllRowsSelect,
            }}
            paginationProps={
              conversationsAsyncStatus !== 'uninitialized' ||
              conversations.length > 0
                ? {
                    previousButtonDisabled:
                      !hasPreviousPage ||
                      conversationsAsyncStatus !== 'fulfilled',
                    previousButtonClick: handlePreviousPage,
                    nextButtonDisabled:
                      !hasNextPage || conversationsAsyncStatus !== 'fulfilled',
                    nextButtonClick: handleNextPage,
                  }
                : undefined
            }
          />
        </Page.Section>
      </Page.Body>
    </Page>
  );
};
